import * as React from "react";
import Text from "antd/lib/typography/Text";
import { AttributeFormConstants, DateFormats } from "../../../common/Constants";
import { Input, Tooltip, DatePicker, Form, Button, Icon, Upload } from "antd";
import { IReferenceValue } from "../../../model/form/ReferenceValue";
import DisplaySelectField from "./DisplaySelectField";
import moment from "moment";
import { DateService } from "../../../service/DateService";
import { IFormInputValueValidationError } from "../../../model/validation/FormInputValueValidationError";
import NumberField from "../app/NumberField";
import NumberFormatter from "../../../model/common/NumberFormatter";
import { IFormQualifierAttributePresentationModel } from "../../../presenter/form/model/FormQualifierAttributePresentationModel";
import { IFormInputValuePresentationModel } from "../../../presenter/form/model/FormInputValuePresentationModel";
import MonthAndYearPicker from "../app/MonthAndYearPicker";
import { Date2Utils } from "../../../common/utils/Date2Utils";
import IntField from "../app/IntField";

export interface State {
  value: any;
  placeholder?: string;
  formInputValue?: IFormInputValuePresentationModel;
  isEditableForm: boolean;
}

export interface Props {
  translate: any;
  attribute: IFormQualifierAttributePresentationModel;
  getReferenceValues: any;
  referenceValueList?: IReferenceValue[];
  isEditableForm: boolean;
  rowIndex: number;
  changeFormAttribute: any;
  validationErrors?: IFormInputValueValidationError[];
  formTypeCode: string;
  qualifierCode: string;
  width?: number | string;
  allowedFileTypes: string;
  isAggregated?: boolean
}

export default class DisplayFormQualifierAttribute extends React.Component<
  Props,
  State
> {
  constructor(props: Props) {
    super(props);
    const { attribute, rowIndex, isEditableForm } = props;

    const formInputValue = attribute?.values
      ? attribute.values[rowIndex]
      : undefined;

    this.state = {
      formInputValue,
      value: formInputValue?.value,
      isEditableForm
    };
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { attribute, rowIndex, isEditableForm } = this.props;

    let formInputValue = attribute?.values
      ? attribute.values[rowIndex]
      : undefined;

    let prevStateFormInputValue = prevState?.formInputValue;

    if (
      formInputValue &&
      prevStateFormInputValue &&
      (formInputValue.value !== prevStateFormInputValue.value ||
        formInputValue?.isRequiredFlagVisible !==
          prevStateFormInputValue.isRequiredFlagVisible ||
        formInputValue?.formAttributeId !==
          prevStateFormInputValue.formAttributeId ||
        isEditableForm !== prevState.isEditableForm)
    ) {
      this.setState({
        value: formInputValue.value,
        formInputValue: formInputValue,
        isEditableForm
      });
    }
  }

  parseDate = (date: Date) => {
    return DateService().formatDate(date);
  };

  getValue = () => {
    return this.state.value;
  };

  setValue = (value: any) => {
    this.setState({ value });
  };

  getDate2Value = (value?: string) => {
    const { translate } = this.props;

    if (value && value.length !== 0) {
      const { month, year } = Date2Utils().parseDate2(value);
      if (month) {
        return `${translate(`months.${month}`)} ${year}`;
      }
      return year;
    }
    return "";
  };

  renderField = (
    attribute: IFormQualifierAttributePresentationModel,
    translate: any,
    getReferenceValues: any,
    changeFormAttribute: any,
    isEditableForm: boolean,
    formTypeCode: string,
    qualifierCode: string,
    allowedFileTypes: string,
    isAggregated: boolean,
    width?: number | string,
    validationErrors?: IFormInputValueValidationError[],
    referenceValueList?: IReferenceValue[],
  ) => {
    const { formInputValue, value } = this.state;

    let label = attribute.labelVisible
      ? attribute.name || attribute.attributeName
      : undefined;

    if(!value && isAggregated){
      label = undefined
    }

    const formInputValueValidationError:
      | IFormInputValueValidationError
      | undefined =
      validationErrors &&
      formInputValue &&
      validationErrors.find(
        (validationError) =>
          validationError &&
          validationError.formAttributeId === formInputValue.formAttributeId
      );

    const validateStatus = formInputValueValidationError ? "error" : undefined;

    let errorMessage = formInputValueValidationError
      ? formInputValueValidationError.messageTranslationCode
        ? translate(formInputValueValidationError.messageTranslationCode)
        : formInputValueValidationError.message
      : undefined;

    const renderErrorMessage = (errorMessage: string) => {
      return <div style={{ marginLeft: 11, marginTop: 2 }}>{errorMessage}</div>;
    };

    const renderRequiredFlag = () => {
      return (
        formInputValue?.isRequiredFlagVisible && (
          <span style={{ color: "red", marginRight: 5, height: 30 }}>
            {formInputValue.isRequiredFlagVisible ? "*" : ""}
          </span>
        )
      );
    };

    const renderNonEditableValue = () => {
      let formattedValue: string | undefined = undefined;

      if (formInputValue?.visible || formInputValue?.visible === undefined) {
        if (
          attribute.attributeType === AttributeFormConstants.TYPE_VALUES.DATE ||
          attribute.attributeType ===
            AttributeFormConstants.TYPE_VALUES.INITIAL_DATE
        ) {
          formattedValue = this.parseDate(value);
        } else if (
          attribute.attributeType ===
            AttributeFormConstants.TYPE_VALUES.NUMBER ||
          attribute.attributeType ===
            AttributeFormConstants.TYPE_VALUES.INITIAL_NUMBER
        ) {
          formattedValue = NumberFormatter().formatReadOnlyValue(value);
        } else if (
          attribute.attributeType === AttributeFormConstants.TYPE_VALUES.DATE_2
        ) {
          formattedValue = this.getDate2Value(value);
        }
      }

      if (attribute.attributeType === AttributeFormConstants.TYPE_VALUES.BLOB) {
        return (
          <Text style={{ fontWeight: 500, width: width || "100%" }}>
            <a
              key="file-download"
              href={`/api/forms/documents/${formInputValue?.formAttributeId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {value}
            </a>
          </Text>
        );
      } else if (
        attribute.attributeType === AttributeFormConstants.TYPE_VALUES.NUMBER ||
        attribute.attributeType ===
          AttributeFormConstants.TYPE_VALUES.INITIAL_NUMBER ||
        attribute.attributeType === AttributeFormConstants.TYPE_VALUES.INT ||
        attribute.attributeType ===
          AttributeFormConstants.TYPE_VALUES.INITIAL_INT
      ) {
        return (
          <Text
            style={{
              fontWeight: 500,
              textAlign: "end",
              width: width || "100%"
            }}
          >
            {formattedValue || value}
          </Text>
        );
      }

      return (
        <Text
          style={{
            fontWeight: 500,
            width: "100%"
          }}
        >
          {formattedValue || value}
        </Text>
      );
    };

    return (
      <div>
        {attribute.attributeType !==
        AttributeFormConstants.TYPE_VALUES.READ_ONLY_REFERENCA ? (
          <Text style={{ fontSize: 12 }}>{label}</Text>
        ) : (
          !isEditableForm && <Text style={{ fontSize: 12 }}>{label}</Text>
        )}
        <div id={formInputValue?.formQualifierId.toString()}>
          {isEditableForm ? (
            attribute.attributeType ===
              AttributeFormConstants.TYPE_VALUES.CHAR ||
            attribute.attributeType ===
              AttributeFormConstants.TYPE_VALUES.INITIAL_CHAR ? (
              <Form.Item
                validateStatus={validateStatus}
                help={renderErrorMessage(errorMessage)}
              >
                <div
                  style={{ display: "flex", flexDirection: "row" }}
                  id={formInputValue?.formAttributeId.toString()}
                >
                  {renderRequiredFlag()}
                  <Input
                    style={{ width }}
                    value={value}
                    maxLength={
                      AttributeFormConstants.TYPE_VALUES.RULES.CHAR_MAX_LENGTH
                    }
                    onBlur={(e) => {
                      const { value } = this.state;
                      changeFormAttribute(
                        formInputValue?.formAttributeId,
                        null,
                        null,
                        null,
                        null,
                        null,
                        null,
                        value,
                        formInputValue?.formQualifierId,
                        null
                      );
                    }}
                    onChange={(e) => this.setValue(e.currentTarget.value)}
                    required={attribute.required}
                    allowClear={true}
                    title={""}
                  />
                </div>
              </Form.Item>
            ) : attribute.attributeType ===
                AttributeFormConstants.TYPE_VALUES.NUMBER ||
              attribute.attributeType ===
                AttributeFormConstants.TYPE_VALUES.INITIAL_NUMBER ? (
              <Form.Item
                validateStatus={validateStatus}
                help={renderErrorMessage(errorMessage)}
              >
                <div
                  style={{ display: "flex", flexDirection: "row" }}
                  id={
                    formInputValue && formInputValue.formAttributeId.toString()
                  }
                >
                  {renderRequiredFlag()}
                  <NumberField
                    style={{ width }}
                    value={value}
                    onBlur={changeFormAttribute}
                    required={attribute.required}
                    name={formInputValue?.formAttributeId}
                    onChange={(e) => this.setValue(e.currentTarget.value)}
                    qualifierId={formInputValue?.formQualifierId}
                  />
                </div>
              </Form.Item>
            ) : attribute.attributeType ===
                AttributeFormConstants.TYPE_VALUES.INT ||
              attribute.attributeType ===
                AttributeFormConstants.TYPE_VALUES.INITIAL_INT ? (
              <Form.Item
                validateStatus={validateStatus}
                help={renderErrorMessage(errorMessage)}
              >
                <div
                  style={{ display: "flex", flexDirection: "row" }}
                  id={
                    formInputValue && formInputValue.formAttributeId.toString()
                  }
                >
                  {renderRequiredFlag()}
                  <IntField
                    style={{ width }}
                    value={value}
                    onBlur={(e) => {
                      changeFormAttribute(
                        formInputValue?.formAttributeId,
                        null,
                        null,
                        null,
                        value,
                        null,
                        null,
                        null,
                        formInputValue?.formQualifierId,
                        null
                      );
                    }}
                    onChange={(value) => this.setValue(value)}
                    required={attribute.required}
                  />
                </div>
              </Form.Item>
            ) : attribute.attributeType ===
                AttributeFormConstants.TYPE_VALUES.DATE ||
              attribute.attributeType ===
                AttributeFormConstants.TYPE_VALUES.INITIAL_DATE ? (
              <Form.Item
                validateStatus={validateStatus}
                help={renderErrorMessage(errorMessage)}
              >
                <div
                  style={{ display: "flex", flexDirection: "row" }}
                  id={
                    formInputValue && formInputValue.formAttributeId.toString()
                  }
                >
                  {renderRequiredFlag()}
                  <DatePicker
                    value={value ? moment(value) : undefined}
                    locale={{ dateFormat: DateFormats.DATE }}
                    style={{ width: width ? width : "100%" }}
                    onChange={(date, dateString) => {
                      this.setValue(date);
                      changeFormAttribute(
                        formInputValue && formInputValue.formAttributeId,
                        null,
                        null,
                        date ? date.toDate() : undefined,
                        null,
                        null,
                        null,
                        null,
                        null,
                        null
                      );
                    }}
                    showToday={false}
                  />
                </div>
              </Form.Item>
            ) : attribute.attributeType ===
              AttributeFormConstants.TYPE_VALUES.DATE_2 ? (
              <Form.Item
                validateStatus={validateStatus}
                help={renderErrorMessage(errorMessage)}
              >
                <div
                  style={{ display: "flex", flexDirection: "row" }}
                  id={
                    formInputValue && formInputValue.formAttributeId.toString()
                  }
                >
                  {renderRequiredFlag()}
                  <MonthAndYearPicker
                    onSelect={(value: string | null) => {
                      changeFormAttribute(
                        formInputValue?.formAttributeId,
                        null,
                        null,
                        null,
                        null,
                        null,
                        null,
                        value,
                        formInputValue?.formQualifierId
                      );
                    }}
                    translate={translate}
                    value={value}
                  />
                </div>
              </Form.Item>
            ) : attribute.attributeType ===
              AttributeFormConstants.TYPE_VALUES.CLOB ? (
              <Form.Item
                validateStatus={validateStatus}
                help={renderErrorMessage(errorMessage)}
              >
                <div
                  style={{ display: "flex", flexDirection: "row" }}
                  id={
                    formInputValue && formInputValue.formAttributeId.toString()
                  }
                >
                  {renderRequiredFlag()}
                  <Input.TextArea
                    style={{ width }}
                    value={value}
                    onBlur={(e) => {
                      const { value } = this.state;
                      changeFormAttribute(
                        formInputValue?.formAttributeId,
                        null,
                        value,
                        null,
                        null,
                        null,
                        null,
                        null,
                        formInputValue?.formQualifierId,
                        null
                      );
                    }}
                    onChange={(e) => {
                      this.setValue(e.currentTarget.value);
                    }}
                    required={attribute.required}
                    allowClear={true}
                    title={""}
                  />
                </div>
              </Form.Item>
            ) : attribute.attributeType ===
                AttributeFormConstants.TYPE_VALUES.REFERENCA ||
              attribute.attributeType ===
                AttributeFormConstants.TYPE_VALUES.INITIAL_REFERENCA ||
              attribute.attributeType ===
                AttributeFormConstants.TYPE_VALUES.REFERENCA_2 ? (
              <Form.Item
                validateStatus={validateStatus}
                help={renderErrorMessage(errorMessage)}
              >
                <div
                  style={{ display: "flex", flexDirection: "row" }}
                  id={
                    formInputValue && formInputValue.formAttributeId.toString()
                  }
                >
                  {renderRequiredFlag()}
                  <DisplaySelectField
                    style={{
                      width: width ? width : 250,
                      flex: width ? undefined : "auto"
                    }}
                    attribute={attribute}
                    translate={translate}
                    getReferenceValues={getReferenceValues}
                    referenceValueList={referenceValueList}
                    formInputValue={formInputValue}
                    onChange={(id, option) => {
                      changeFormAttribute(
                        formInputValue?.formAttributeId,
                        null,
                        null,
                        null,
                        null,
                        id,
                        option?.props.children,
                        null,
                        formInputValue?.formQualifierId,
                        null
                      );
                    }}
                    formTypeCode={formTypeCode}
                    qualifierCode={qualifierCode}
                    isReference2={
                      attribute.attributeType ===
                      AttributeFormConstants.TYPE_VALUES.REFERENCA_2
                    }
                  />
                </div>
              </Form.Item>
            ) : attribute.attributeType ===
              AttributeFormConstants.TYPE_VALUES.READ_ONLY_REFERENCA ? (
              <div
                style={{ display: "flex", flexDirection: "row" }}
                id={formInputValue && formInputValue.formAttributeId.toString()}
              >
                {renderRequiredFlag()}
                <Text>{value}</Text>
              </div>
            ) : attribute.attributeType ===
              AttributeFormConstants.TYPE_VALUES.BLOB ? (
              <Form.Item
                validateStatus={validateStatus}
                help={renderErrorMessage(errorMessage)}
              >
                <div
                  style={{ display: "flex", flexDirection: "row" }}
                  id={
                    formInputValue && formInputValue.formAttributeId.toString()
                  }
                >
                  {renderRequiredFlag()}
                  <Upload
                    accept={allowedFileTypes || "*.*"}
                    multiple={false}
                    beforeUpload={(file) => {
                      return false;
                    }}
                    fileList={
                      value
                        ? [
                            {
                              uid: "-1",
                              status: "done",
                              name: (
                                <a
                                  key="file-download"
                                  href={`/api/forms/documents/${formInputValue?.formAttributeId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {value}
                                </a>
                              )
                            } as any
                          ]
                        : undefined
                    }
                    onChange={({ file, fileList }) => {
                      if (file.status === "removed") {
                        changeFormAttribute(
                          formInputValue?.formAttributeId,
                          null,
                          null,
                          null,
                          null,
                          null,
                          null,
                          null,
                          formInputValue?.formQualifierId,
                          null
                        );
                      } else {
                        changeFormAttribute(
                          formInputValue?.formAttributeId,
                          file,
                          null,
                          null,
                          null,
                          null,
                          null,
                          file.name,
                          formInputValue?.formQualifierId,
                          file.type
                        );
                      }
                    }}
                    showUploadList={{
                      showDownloadIcon: false
                    }}
                  >
                    <Button>
                      <Icon type={"upload"} />
                      {translate("form_configuration.upload")}
                    </Button>
                  </Upload>
                </div>
              </Form.Item>
            ) : undefined
          ) : (
            (value || value === 0) && (
              <div
                style={{ display: "flex", flexDirection: "row" }}
                id={formInputValue && formInputValue.formAttributeId.toString()}
              >
                {renderRequiredFlag()}
                {renderNonEditableValue()}
              </div>
            )
          )}
        </div>
      </div>
    );
  };

  render() {
    const {
      translate,
      attribute,
      getReferenceValues,
      referenceValueList,
      isEditableForm,
      changeFormAttribute,
      validationErrors,
      formTypeCode,
      qualifierCode,
      allowedFileTypes,
      width,
      isAggregated = false
    } = this.props;

    const tooltipVisible = attribute.tooltip ? true : false;

    return (
      <div style={{ paddingTop: 3, paddingBottom: 3 }}>
        {tooltipVisible ? (
          <Tooltip title={attribute.tooltip}>
            {this.renderField(
              attribute,
              translate,
              getReferenceValues,
              changeFormAttribute,
              isEditableForm,
              formTypeCode,
              qualifierCode,
              allowedFileTypes,
              isAggregated,
              width,
              validationErrors,
              referenceValueList
            )}
          </Tooltip>
        ) : (
          this.renderField(
            attribute,
            translate,
            getReferenceValues,
            changeFormAttribute,
            isEditableForm,
            formTypeCode,
            qualifierCode,
            allowedFileTypes,
            isAggregated,
            width,
            validationErrors,
            referenceValueList
          )
        )}
      </div>
    );
  }
}
